"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { useOptionalGsbNavigation } from "@gsb-core/app-kit";
import { CornerDownLeft, Loader2, Search } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList, Dialog, DialogContent, DialogDescription, DialogTitle, } from "@gsb-core/react-components";
import { Command as CommandPrimitive } from "cmdk";
import { cn } from "../utils/cn";
import { useHotkeys } from "../hooks/use-hotkeys";
import { Kbd } from "./kbd";
import { useCommandRegistry } from "./command-registry";
const DEBOUNCE_MS = 140;
function useDebounced(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}
function isSubsequence(haystack, needle) {
    let index = 0;
    for (const character of haystack) {
        if (character === needle[index])
            index += 1;
        if (index === needle.length)
            return true;
    }
    return needle.length === 0;
}
const WORD_CHARACTER = /[\p{L}\p{N}]/u;
/** Best tier over every occurrence: whole word beats word start beats substring. */
function occurrenceScore(name, query) {
    let best = 0;
    for (let index = name.indexOf(query); index !== -1; index = name.indexOf(query, index + 1)) {
        const before = index === 0;
        const startsWord = before || !WORD_CHARACTER.test(name[index - 1] ?? "");
        const after = name[index + query.length];
        const endsWord = after === undefined || !WORD_CHARACTER.test(after);
        const tier = before
            ? endsWord
                ? 0.95
                : 0.9
            : startsWord
                ? endsWord
                    ? 0.85
                    : 0.8
                : 0.7;
        best = Math.max(best, tier);
    }
    return best;
}
/**
 * Ranks an exact label above a whole-word match, a whole word above a word start,
 * and a word start above a substring, so "product" beats "product variant" beats
 * "production log". Items pass `keywords` as `[label, ...aliases]` because cmdk
 * the command `value` must stay unique.
 */
export function scoreCommand(_value, search, keywords) {
    const query = search.trim().toLowerCase();
    if (query === "")
        return 1;
    const [label = "", ...aliases] = keywords ?? [];
    const name = label.toLowerCase();
    const extra = aliases.join(" ").toLowerCase();
    const haystack = `${name} ${extra}`;
    const tokens = query.split(/\s+/).filter(Boolean);
    const base = (() => {
        if (name === query)
            return 1;
        const direct = occurrenceScore(name, query);
        if (direct > 0)
            return direct;
        if (tokens.every((token) => name.includes(token)))
            return 0.6;
        if (tokens.every((token) => haystack.includes(token)))
            return 0.4;
        return isSubsequence(haystack, query) ? 0.15 : 0;
    })();
    if (base === 0)
        return 0;
    // Shorter labels win ties: "Product" outranks "Product variant".
    return base + 0.04 * (1 - Math.min(name.length, 60) / 60);
}
function PaletteItem({ action, forceMount, onRun, }) {
    const Icon = action.icon;
    return (_jsxs(CommandItem, { forceMount: forceMount, value: action.id, onSelect: () => {
            onRun();
        }, className: cn("group h-10 gap-3 rounded-md px-3 text-body-sm", "data-[selected=true]:bg-primary-subtle data-[selected=true]:text-content-brand", action.destructive === true &&
            "text-danger data-[selected=true]:bg-danger-bg data-[selected=true]:text-danger-fg"), children: [Icon !== undefined ? (_jsx(Icon, { className: "h-4 w-4 shrink-0 text-content-tertiary group-data-[selected=true]:text-current" })) : null, _jsx("span", { className: "truncate font-medium", children: action.label }), action.description !== undefined ? (_jsx("span", { className: "truncate text-caption text-content-tertiary", children: action.description })) : null, _jsxs("span", { className: "ms-auto flex items-center gap-1", children: [action.shortcut !== undefined ? _jsx(Kbd, { combo: action.shortcut }) : null, _jsx(CornerDownLeft, { className: "h-3.5 w-3.5 text-content-tertiary opacity-0 group-data-[selected=true]:opacity-100" })] })] }));
}
/**
 * ⌘K palette. Static actions are filtered locally by cmdk; registered sources are
 * queried asynchronously and merged in, which is what lets the palette resolve
 * entities and rows rather than just menu items.
 */
export function CommandPalette({ placeholder = "Search or run a command…", recentActionIds = [], fallback, }) {
    const { actions, sources, open, setOpen, query, setQuery } = useCommandRegistry();
    const navigation = useOptionalGsbNavigation();
    const [remote, setRemote] = useState(null);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const focusReturnRef = useRef(null);
    const pendingActionRef = useRef(null);
    const debouncedQuery = useDebounced(query, DEBOUNCE_MS);
    const runCommand = (action) => {
        if (action.navigates && inputRef.current) {
            navigation?.prepareNavigationFocus(inputRef.current);
        }
        focusReturnRef.current = null;
        pendingActionRef.current = action;
        setOpen(false);
    };
    useHotkeys([
        { combo: "mod+k", handler: () => setOpen(!open), enableInFormFields: true },
        { combo: "mod+/", handler: () => setOpen(!open), enableInFormFields: true },
    ]);
    useEffect(() => {
        if (!open) {
            setQuery("");
            setRemote(null);
        }
    }, [open, setQuery]);
    useEffect(() => {
        if (!open ||
            query !== debouncedQuery ||
            query.trim().length < 2 ||
            sources.length === 0) {
            setRemote(null);
            setLoading(false);
            return;
        }
        const controller = new AbortController();
        setLoading(true);
        void Promise.allSettled(sources.map((source) => source.search(debouncedQuery, controller.signal)))
            .then((results) => {
            if (controller.signal.aborted)
                return;
            setRemote({
                query: debouncedQuery,
                actions: results.flatMap((result) => result.status === "fulfilled" ? [...result.value] : []),
            });
        })
            .finally(() => {
            if (!controller.signal.aborted)
                setLoading(false);
        });
        return () => controller.abort();
    }, [query, debouncedQuery, open, sources]);
    const visible = useMemo(() => {
        const base = query.length === 0 && recentActionIds.length > 0
            ? [
                ...actions.filter((action) => recentActionIds.includes(action.id)),
                ...actions.filter((action) => !recentActionIds.includes(action.id)),
            ]
            : actions;
        const candidates = [
            ...base,
            ...(remote?.query === query && query.trim().length >= 2
                ? remote.actions
                : []),
        ];
        if (query.trim() === "")
            return candidates;
        return candidates
            .map((action) => ({
            action,
            score: scoreCommand(action.id, query, [
                action.label,
                action.description ?? "",
                ...(action.keywords ?? []),
            ]),
        }))
            .filter(({ score }) => score > 0)
            .sort((first, second) => second.score - first.score)
            .map(({ action }) => action);
    }, [actions, remote, query, recentActionIds]);
    const grouped = useMemo(() => {
        const map = new Map();
        for (const action of visible) {
            const bucket = map.get(action.group);
            if (bucket === undefined)
                map.set(action.group, [action]);
            else
                bucket.push(action);
        }
        return [...map.entries()];
    }, [visible]);
    const fallbackAction = useMemo(() => (query.trim() === "" ? null : (fallback?.(query.trim()) ?? null)), [fallback, query]);
    return (_jsx(Dialog, { open: open, onOpenChange: setOpen, children: _jsxs(DialogContent, { className: "max-w-2xl gap-0 p-0 shadow-2xl [&>button]:hidden", 
            // This combobox is the dialog itself, not a child picker whose Escape
            // should be contained by the shared dialog's expanded-combobox guard.
            onEscapeKeyDown: () => setOpen(false), onOpenAutoFocus: (event) => {
                focusReturnRef.current =
                    document.activeElement instanceof HTMLElement
                        ? document.activeElement
                        : null;
                if (query.length > 0 && inputRef.current) {
                    event.preventDefault();
                    inputRef.current.focus();
                    inputRef.current.setSelectionRange(query.length, query.length);
                }
            }, onCloseAutoFocus: (event) => {
                const action = pendingActionRef.current;
                pendingActionRef.current = null;
                if (action) {
                    event.preventDefault();
                    void action.perform();
                    return;
                }
                const target = focusReturnRef.current;
                focusReturnRef.current = null;
                if (!target?.isConnected)
                    return;
                event.preventDefault();
                target.focus({ preventScroll: true });
            }, children: [_jsx(DialogTitle, { className: "sr-only", children: "Command palette" }), _jsx(DialogDescription, { className: "sr-only", children: "Search across the workspace and run commands with the keyboard." }), _jsxs(Command, { shouldFilter: false, loop: true, className: "bg-surface-overlay [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-caption [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-content-tertiary", children: [_jsxs("div", { className: "flex items-center gap-3 border-b border-line px-4", children: [loading ? (_jsx(Loader2, { className: "h-4 w-4 shrink-0 animate-spin text-content-tertiary", "aria-hidden": true })) : (_jsx(Search, { className: "h-4 w-4 shrink-0 text-content-tertiary", "aria-hidden": true })), _jsx(CommandPrimitive.Input, { ref: inputRef, value: query, onValueChange: setQuery, placeholder: placeholder, className: "h-14 w-full bg-transparent text-body outline-none placeholder:text-content-tertiary" }), _jsx(Kbd, { combo: "escape" })] }), _jsxs(CommandList, { className: "max-h-[26rem] scrollbar-thin px-2 pb-2", children: [fallbackAction === null ? (_jsxs(CommandEmpty, { className: "px-4 py-10 text-center text-body-sm text-content-tertiary", children: ["No matches for \u201C", query, "\u201D."] })) : null, grouped.map(([group, items]) => (_jsx(CommandGroup, { heading: group, children: items.map((action) => (_jsx(PaletteItem, { action: action, onRun: () => runCommand(action) }, action.id))) }, group))), fallbackAction !== null ? (_jsx(CommandGroup, { forceMount: true, heading: fallbackAction.group, children: _jsx(PaletteItem, { forceMount: true, action: fallbackAction, onRun: () => runCommand(fallbackAction) }) })) : null] }), _jsxs("footer", { className: "flex items-center gap-4 border-t border-line bg-surface-sunken px-4 py-2 text-caption text-content-tertiary", children: [_jsxs("span", { className: "flex items-center gap-1.5", children: [_jsx(Kbd, { combo: "\u2191" }), _jsx(Kbd, { combo: "\u2193" }), "navigate"] }), _jsxs("span", { className: "flex items-center gap-1.5", children: [_jsx(Kbd, { combo: "enter" }), "run"] }), _jsxs("span", { className: "ms-auto flex items-center gap-1.5", children: [_jsx(Kbd, { combo: "shift+?" }), "all shortcuts"] })] })] })] }) }));
}
//# sourceMappingURL=command-palette.js.map